/**
 * Booking Timezones
 *
 * The public booking flow used to render EVERY time in `America/Chicago` with
 * no label and no way to change it, so a prospect in Denver read "2:00 PM" and
 * showed up an hour late (or not at all). These helpers let the flow show times
 * in the BOOKER's zone, labelled, with an explicit picker.
 *
 * Everything here is pure and takes an explicit reference date where the answer
 * depends on the calendar (DST), so behavior is testable without freezing time.
 */

import { DEFAULT_TIMEZONE } from './bookingConstants';

export interface TimeZoneOption {
  /** IANA zone id, e.g. `America/Denver`. */
  value: string;
  /** Human label for the option list, e.g. "Mountain Time — Denver". */
  label: string;
  /** Coarse grouping for an optgroup-style list. */
  region: string;
}

/**
 * Curated zone list. Deliberately short: WTC books North America almost
 * exclusively, with a tail of international prospects. Anything not listed
 * still works — {@link getTimeZoneOptions} appends the booker's detected zone
 * when it is missing, so no one is forced into the wrong offset.
 */
export const CURATED_TIME_ZONES: readonly TimeZoneOption[] = [
  { value: 'America/New_York', label: 'Eastern Time — New York', region: 'United States' },
  { value: 'America/Chicago', label: 'Central Time — Chicago', region: 'United States' },
  { value: 'America/Denver', label: 'Mountain Time — Denver', region: 'United States' },
  { value: 'America/Phoenix', label: 'Mountain Time (no DST) — Phoenix', region: 'United States' },
  { value: 'America/Los_Angeles', label: 'Pacific Time — Los Angeles', region: 'United States' },
  { value: 'America/Anchorage', label: 'Alaska Time — Anchorage', region: 'United States' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time — Honolulu', region: 'United States' },
  { value: 'America/Toronto', label: 'Eastern Time — Toronto', region: 'Canada' },
  { value: 'America/Vancouver', label: 'Pacific Time — Vancouver', region: 'Canada' },
  { value: 'America/Mexico_City', label: 'Central Time — Mexico City', region: 'Latin America' },
  { value: 'America/Sao_Paulo', label: 'Brasília Time — São Paulo', region: 'Latin America' },
  { value: 'Europe/London', label: 'United Kingdom — London', region: 'Europe' },
  { value: 'Europe/Dublin', label: 'Ireland — Dublin', region: 'Europe' },
  { value: 'Europe/Paris', label: 'Central European Time — Paris', region: 'Europe' },
  { value: 'Europe/Berlin', label: 'Central European Time — Berlin', region: 'Europe' },
  { value: 'Asia/Dubai', label: 'Gulf Time — Dubai', region: 'Asia & Pacific' },
  { value: 'Asia/Kolkata', label: 'India — Kolkata', region: 'Asia & Pacific' },
  { value: 'Asia/Singapore', label: 'Singapore', region: 'Asia & Pacific' },
  { value: 'Asia/Tokyo', label: 'Japan — Tokyo', region: 'Asia & Pacific' },
  { value: 'Australia/Sydney', label: 'Australia — Sydney', region: 'Asia & Pacific' },
];

/** Group order used when rendering the picker. */
export const TIME_ZONE_REGION_ORDER: readonly string[] = [
  'United States',
  'Canada',
  'Latin America',
  'Europe',
  'Asia & Pacific',
  'Other',
];

/**
 * True when the runtime accepts `zone` as an IANA time zone. Guards against a
 * stale `?tz=` query param or an exotic browser value crashing every
 * `formatInTimeZone` call downstream.
 */
export function isValidTimeZone(zone: string | null | undefined): boolean {
  if (!zone || typeof zone !== 'string') return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

/**
 * The booker's own zone, or {@link DEFAULT_TIMEZONE} when it cannot be
 * determined (SSR, or a runtime without a resolved zone). Never throws.
 */
export function detectBrowserTimeZone(): string {
  try {
    const resolved = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return isValidTimeZone(resolved) ? resolved : DEFAULT_TIMEZONE;
  } catch {
    return DEFAULT_TIMEZONE;
  }
}

/**
 * Short zone abbreviation for the given instant, e.g. `CDT` in summer and
 * `CST` in winter. Some zones have no alphabetic abbreviation and report a
 * numeric one (`GMT+5:30`) — that is returned as-is, which is still correct.
 */
export function formatTimeZoneAbbreviation(
  zone: string,
  referenceDate: Date = new Date()
): string {
  const safeZone = isValidTimeZone(zone) ? zone : DEFAULT_TIMEZONE;
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: safeZone,
      timeZoneName: 'short',
    }).formatToParts(referenceDate);
    return parts.find((part) => part.type === 'timeZoneName')?.value ?? safeZone;
  } catch {
    return safeZone;
  }
}

/**
 * Zone id rendered for humans — `America/Los_Angeles` → `Los Angeles`. Used
 * when the zone is not in {@link CURATED_TIME_ZONES} and so has no hand label.
 */
export function humanizeTimeZoneId(zone: string): string {
  const tail = zone.split('/').slice(-1)[0] ?? zone;
  return tail.replace(/_/g, ' ');
}

/**
 * One-line label for the CURRENT selection, e.g.
 * `Central Time — Chicago (CDT)`. This is the string shown next to every set
 * of times so a booker can never misread an hour.
 */
export function formatTimeZoneDisplay(
  zone: string,
  referenceDate: Date = new Date()
): string {
  const safeZone = isValidTimeZone(zone) ? zone : DEFAULT_TIMEZONE;
  const curated = CURATED_TIME_ZONES.find((option) => option.value === safeZone);
  const name = curated?.label ?? humanizeTimeZoneId(safeZone);
  return `${name} (${formatTimeZoneAbbreviation(safeZone, referenceDate)})`;
}

/**
 * The picker's options: the curated list, plus `selectedZone` appended under
 * "Other" when it is a valid zone the list does not already cover. Invalid
 * input is ignored rather than injected, so the select can never hold a value
 * that breaks formatting.
 */
export function getTimeZoneOptions(selectedZone?: string | null): TimeZoneOption[] {
  const options = [...CURATED_TIME_ZONES];
  if (
    selectedZone &&
    isValidTimeZone(selectedZone) &&
    !options.some((option) => option.value === selectedZone)
  ) {
    options.push({
      value: selectedZone,
      label: humanizeTimeZoneId(selectedZone),
      region: 'Other',
    });
  }
  return options;
}

/** Options bucketed by region, in {@link TIME_ZONE_REGION_ORDER}. */
export function getGroupedTimeZoneOptions(
  selectedZone?: string | null
): Array<{ region: string; options: TimeZoneOption[] }> {
  const options = getTimeZoneOptions(selectedZone);
  return TIME_ZONE_REGION_ORDER.map((region) => ({
    region,
    options: options.filter((option) => option.region === region),
  })).filter((group) => group.options.length > 0);
}

/**
 * Resolve the zone the flow should start in: an explicit `?tz=` param wins (so
 * a rep can send a link pinned to the prospect's zone), then the browser's own
 * zone, then the business default.
 */
export function resolveInitialTimeZone(paramZone?: string | null): string {
  if (isValidTimeZone(paramZone)) return paramZone as string;
  return detectBrowserTimeZone();
}
